import type {
  EmojiProvider as EmojiProviderInterface,
  EmojiId,
  EmojiDescription,
} from '@atlaskit/emoji/types';

import { emojis, meta } from '@canvas/emoji-data';

import { type OnProviderChange } from '../types';

export class EmojiProvider implements EmojiProviderInterface {
  private listeners = new Set<OnProviderChange['result']>();

  filter(query?: string): void {
    this.listeners.forEach((listener) => {
      listener({
        emojis: emojis
          .filter((e) => {
            return (
              !query?.slice(1) ||
              [e.name, e.shortName, e.ascii, e.fallback, e.category].some((i) =>
                i?.includes(query.slice(1))
              )
            );
          })
          .map((e) => ({
            ...e,
            representation: {
              ...e.representation,
              sprite: meta.spriteSheets[e.representation.spriteRef],
            },
          })),
        query,
      });
    });

    return;
  }

  subscribe(onChange: OnProviderChange): void {
    this.listeners.add(onChange.result);
  }

  unsubscribe(onChange: OnProviderChange): void {
    this.listeners.delete(onChange.result);
  }

  async getAsciiMap(): Promise<Map<string, EmojiDescription>> {
    return new Map();
  }

  async fetchByEmojiId(input: EmojiId): Promise<EmojiDescription | undefined> {
    return this.findByEmojiId(input);
  }

  async findByEmojiId(input: EmojiId): Promise<EmojiDescription | undefined> {
    const e = emojis.find((e) => e.id === input.id || e.shortName === input.shortName);

    if (e) {
      return {
        ...e,
        representation: {
          ...e.representation,
          sprite: meta.spriteSheets[e.representation.spriteRef],
        },
      };
    }
  }

  getSelectedTone() {
    return 0;
  }

  /**
   * Returns the logged user passed by the Product
   */
  getCurrentUser(): undefined {
    return;
  }

  /**
   * Whiteboards doesn't need these methods.
   * Previously the EmojiProvider was typed as `any` and these were just ignored.
   * Now they are added for type safety but for consistency are mostly ignored.
   * In most cases these probably shouldn't even be public methods and are used under
   * the hood.
   */
  async findByShortName(): Promise<EmojiDescription | undefined> {
    return;
  }

  async findById(): Promise<EmojiDescription | undefined> {
    return;
  }

  getMediaEmojiDescriptionURLWithInlineToken(
    emoji: EmojiDescription
  ): Promise<EmojiDescription> {
    return Promise.resolve(emoji);
  }

  async findInCategory(): Promise<EmojiDescription[]> {
    return [];
  }

  async getFrequentlyUsed() {
    return [];
  }

  async deleteSiteEmoji() {
    return false;
  }

  loadMediaEmoji(): undefined {
    return;
  }

  optimisticMediaRendering() {
    return false;
  }

  async fetchEmojiProvider(): Promise<undefined> {
    return;
  }

  getOptimisticImageURL(): undefined {
    return;
  }

  setSelectedTone() {}
}

export const emojiProvider = Promise.resolve(new EmojiProvider());
